/**
 * @file        src/pages/hotel/VoucherDoc.jsx
 * @description Admin-side HOTEL VOUCHER — rendered INSIDE the console (no redirect
 *              to the customer site) but a faithful visual port of the customer
 *              voucher (booking_frontend VoucherShell + HotelVoucher): white A4
 *              sheet, status pill header, Hotel Details, Rooms & Guests, Lead Guest
 *              & Contact, Price Summary, Cancellation Policy, Booking Notes and the
 *              standard terms. The admin has no Tailwind, so the design is ported
 *              with inline styles matching the customer palette. Fed by the admin
 *              GET /hotel/bookings/:id/details.
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Price Summary: Room charges include the processing fee; Convenience fee in its place.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-09-23 Claude — "Back to Bookings" returns to the list this document
 *                          was opened from. This route is shared by the B2C and
 *                          the Corporate screen, so Back always landed on the
 *                          B2C list; lib/docOrigin carries which it was.
 *   - 2026-09-21 Claude — The sheet is `.doc-sheet` (+ .doc-sheet-voucher for the
 *                          voucher's own flush gutters and narrower width), like
 *                          the other three document pages. It was styled entirely
 *                          inline and carried NO class, and .doc-sheet is the
 *                          signal ShareEmailModal polls for to know an off-screen
 *                          document has finished loading — so "Share by Email →
 *                          Voucher" could only ever end in "Timed out preparing
 *                          the document." after its 15s budget, for every hotel
 *                          booking. The class also applies the @media print and
 *                          .share-capture resets (no floating-card chrome, full
 *                          bleed) that the emailed PDF needs.
 *   - 2026-09-16 Claude — Dropped the "Hotel Conf. #…" chip from the Hotel Details
 *                          header. It was NOT a hotel confirmation number: it
 *                          printed String(booking_reference).slice(-10), i.e. the
 *                          tail of our own Booking ID, which the sheet already
 *                          shows in full two fields above (TJ207102912203 →
 *                          "#7102912203"). The property holds no such number.
 *                          Nothing in either backend stores the hotel's real
 *                          confirmation number, so there is no correct value to
 *                          print — and the customer's own voucher renders no chip
 *                          at all on a by-ref view (it guards on the search
 *                          store's hotel id, deliberately dropped for a specific
 *                          booking). Admin and customer copies of the same stay
 *                          now agree in front of a hotel front desk.
 *                          (Google Maps, new tab), matching the customer voucher
 *                          on both storefronts. Searches hotel name + the full
 *                          joined address the admin API returns (street, city,
 *                          state, country); the admin API carries no coordinates.
 *                          Rendered ONLY when an address exists — a name-only
 *                          search can pin a same-named property in another city.
 *   - 2026-09-10 Claude — Heading reads "Hotel Confirmation" (was
 *                          "Accommodation Confirmation"), matching the customer
 *                          copy on both storefronts.
 *   - 2026-09-10 Claude — Voucher No. now matches the customer's copy of the same
 *                          stay (NH-HTL-<hotel_bookings.id>); it was slicing the
 *                          supplier reference, so admin and customer quoted two
 *                          different numbers. PAN no longer prints against each
 *                          guest — it belongs on the invoice, not on a sheet
 *                          handed to a hotel front desk.
 *   - 2026-09-09 Claude — Header always renders a LOGO, via the shared
 *                          components/DocBrandMark (storefront logo → tenant
 *                          admin-portal logo → the green BrandWordmark SVG the
 *                          customer site shows). Neither logo column is
 *                          populated on most tenants, so in practice every
 *                          voucher printed the plain-text COMPANY.name wordmark.
 *   - 2026-09-08 Claude — Header prints the booking's own storefront logo
 *                          (brands.logo_url) when configured, falling back to the
 *                          tenant name wordmark — matching the flight e-ticket.
 *   - 2026-08-04 Claude — CR D2: accepts an optional `bookingIdOverride` prop
 *                          that short-circuits useParams() — see flight
 *                          TicketDoc.jsx for why (ShareEmailModal off-screen PDF
 *                          capture).
 *   - 2026-07-16 Claude — Initial (admin-native voucher; renders in-console, no
 *                          redirect, visually identical to the customer voucher).
 *   - 2026-08-10 Claude — Hard status gate via docAccess(): a voucher only
 *                          exists for a CONFIRMED stay (client-approved rule) —
 *                          pending / cancellation-pending / cancelled bookings
 *                          now get a clear "not available" notice instead of a
 *                          printable voucher with a warning banner. The banner
 *                          block is gone (unreachable behind the gate). Fee
 *                          lines (taxes / processing / convenience) always
 *                          itemise instead of hiding at zero.
 *   - 2026-08-10 Claude — Header wordmark renders COMPANY.name instead of the
 *                          literal "NEON HOLIDAYS", so a Beyond voucher stops
 *                          showing Neon as the issuer (the neon-green accent
 *                          it was tinted with went with it). See
 *                          lib/useCompany.js + migration 051.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../lib/docOrigin';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { getBookingDetails } from '../../api/hotelBookings';
import { docAccess } from './cells';
import { useCompany } from '../../lib/useCompany';
import DocBrandMark from '../../components/DocBrandMark';

const INK = '#0A0F1E';
const GREEN = '#0F9D6B';
const LINE = '#e2e8f0';
const SLATE50 = '#f8fafc';
const SLATE400 = '#94a3b8';
const SLATE500 = '#64748b';
const SLATE600 = '#475569';
const SLATE700 = '#334155';

const inr2 = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const HOTEL_BOOKING_TERMS = [
  'Each country/state may have its own COVID-19 guidelines and restrictions. Please check with the hotel or the country/state website.',
  'Each booking is applicable of ₹20 per room per night non-refundable service fees.',
  'Your booking is confirmed. However, your name will be listed in the hotel’s reservation system closer to your arrival date.',
  'Guest photo ID must be presented at the time of check-in.',
  'Credit card or cash deposit may be required for extra services at the time of check-in.',
  'All extra charges will be borne by the guest directly prior to departure.',
  'Extra-person and/or extra-bed charges may apply and vary depending on property policy.',
  'Special requests (bed type, smoking room, early check-in, late check-out, etc.) are subject to availability at check-in and are not guaranteed.',
  'Full cancellation charges are applicable on early check-out unless otherwise specified.',
  'City tax and resort fee (if any) are to be paid directly to the hotel.',
  'Additional GST payment (if any) to be paid to the hotel directly by the guest.',
  'As per RBI guidelines, for foreign nationals a passport copy of the guest is mandatory. Failure to comply may result in cancellation without notice.',
];

function statusStyle(status = '') {
  const s = String(status).toLowerCase();
  if (/book|confirm|success/.test(s)) return { bg: 'rgba(34,197,94,.12)', fg: '#15803d', label: 'Confirmed' };
  if (/pending|await|initiat|request/.test(s)) return { bg: 'rgba(245,158,11,.12)', fg: '#b45309', label: /request/.test(s) ? 'Cancellation Requested' : 'Pending' };
  if (/fail|cancel/.test(s)) return { bg: 'rgba(239,68,68,.12)', fg: '#b91c1c', label: 'Cancelled' };
  return { bg: 'rgba(52,239,164,.12)', fg: GREEN, label: status || 'Processing' };
}

const Sec = ({ children }) => (
  <h2 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: GREEN, margin: '24px 0 12px' }}>{children}</h2>
);
const Field = ({ label, value, mono }) => (value == null || value === '') ? null : (
  <div style={{ minWidth: 0 }}>
    <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.03em', color: SLATE400, fontWeight: 600, margin: 0 }}>{label}</p>
    <p style={{ fontSize: 14, fontWeight: 600, color: INK, margin: '2px 0 0', wordBreak: 'break-word', fontFamily: mono ? 'ui-monospace, monospace' : undefined }}>{value}</p>
  </div>
);
const Grid = ({ cols = 4, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 16 }}>{children}</div>
);
const Row = ({ k, v }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${SLATE50}` }}>
    <span style={{ color: SLATE600 }}>{k}</span>
    <span style={{ fontWeight: 600, color: INK }}>{v}</span>
  </div>
);

const guestLabel = (t, i) => `${t.title ? `${t.title}. ` : ''}${[t.firstName, t.lastName].filter(Boolean).join(' ')}`.trim() || `Guest ${i + 1}`;

export default function VoucherDoc({ bookingIdOverride } = {}) {
  const { id: routeId } = useParams();
  const id = bookingIdOverride ?? routeId;
  const navigate = useNavigate();
  // Which list to return to — this document route is shared by the B2C and the
  // Corporate screen (lib/docOrigin).
  const { search } = useLocation();
  const COMPANY = useCompany();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['hotel-booking-details', Number(id)],
    queryFn: () => getBookingDetails(id),
  });
  const b = data?.data;
  useEffect(() => { if (b) document.title = `Hotel Voucher ${b.booking_reference} · ${COMPANY.name}`; }, [b]);

  if (isLoading) return <div className="panel glass"><div className="empty">Preparing voucher…</div></div>;
  if (isError || !b) return <div className="panel glass"><div className="empty">Could not load this booking.</div></div>;

  // A voucher only exists for a CONFIRMED stay (client-approved rule) — gated
  // here too, not just at the buttons, so a pasted URL can't print one.
  if (!docAccess(b.booking_status).voucher) {
    return (
      <div className="panel glass">
        <div className="empty">
          A voucher is only issued for a confirmed stay — this booking is “{b.booking_status || 'not confirmed'}”.
          <div style={{ marginTop: 12 }}>
            <button className="btn ghost" onClick={() => navigate(backToList(search, '/hotel/bookings', '/hotel/corporate-bookings'))}>
              <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ref = b.booking_reference || '';
  // Voucher No. MUST be the same string the customer's own voucher prints —
  // OUR hotel_bookings.id ("NH-HTL-204"), which is the identifier support can
  // actually look a booking up by. This used to slice the last 8 characters of
  // the supplier reference ("NH-HTL-02965710"), so the admin copy and the
  // customer copy of the SAME stay quoted two different voucher numbers.
  // (booking_frontends/*/src/pages/Voucher/HotelVoucher.jsx keeps the same rule.)
  const voucherNo = b.id
    ? `NH-HTL-${b.id}`
    : 'NH-HTL-' + String(ref).replace(/[^A-Za-z0-9]/g, '').slice(-8).toUpperCase();
  const st = statusStyle(b.booking_status);
  const hotel = b.hotel || {};
  const stay = b.stay || {};
  const rooms = b.rooms || [];
  const fare = b.fare || {};
  const nights = stay.nights || 1;
  const roomCount = stay.rooms || rooms.length || 1;
  const adults = rooms.reduce((a, r) => a + (r.adults || 0), 0) || stay.guests || 1;
  const children = rooms.reduce((a, r) => a + (r.children || 0), 0);
  const meal = rooms[0]?.mealBasis || 'Room Only';
  const refundable = b.cancellation_policy ? b.cancellation_policy.isRefundable : undefined;
  const penalties = b.cancellation_policy?.penalties || [];
  const notes = b.booking_notes || [];
  const leadName = b.lead_guest
    ? [b.lead_guest.firstName, b.lead_guest.lastName].filter(Boolean).join(' ')
    : (rooms[0]?.travellers?.[0] ? guestLabel(rooms[0].travellers[0], 0) : 'Guest');

  // "Get Directions" -> Google Maps, same rule as the customer voucher: name + the
  // full joined address, never the name alone (that can pin a same-named hotel in
  // another city). No address -> null, and the link is not rendered.
  const hotelName = hotel.name || stay.hotelName || '';
  const directionsUrl = hotel.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [hotelName, hotel.address].filter(Boolean).join(', '),
      )}`
    : null;

  const box = { border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden' };
  const boxHead = { padding: '12px 16px', background: SLATE50 };

  return (
    <>
      <div className="doc-bar">
        <button className="btn ghost" onClick={() => navigate(backToList(search, '/hotel/bookings', '/hotel/corporate-bookings'))}>
          <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
        </button>
        <button className="btn primary" onClick={() => window.print()}>
          <Icon name="download" /> Print / Save as PDF
        </button>
      </div>

      {/* Width, radius, shadow and background come from .doc-sheet now: leaving
          them inline would outrank `.share-capture .doc-sheet`, and the emailed
          PDF would be a 768px rounded card floating in a 1000px white page. */}
      <article className="doc-sheet doc-sheet-voucher" style={{ color: INK, fontFamily: 'Inter, system-ui, sans-serif' }}>
        {/* header */}
        <header style={{ padding: '24px 32px', borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            {/* Issuing brand mark — DocBrandMark owns the storefront → tenant →
                bundled-wordmark fallback chain, shared with the invoices. */}
            {/* marginBottom 0: this header spaces the address line itself
                (marginTop 10), so .doc-brand-logo's own 10px would double it. */}
            <DocBrandMark brandCode={b.brand} className="doc-brand-logo" style={{ marginBottom: 0 }} />
            <p style={{ fontSize: 11, color: SLATE500, marginTop: 10, maxWidth: 320, lineHeight: 1.5 }}>{COMPANY.address}</p>
            <p style={{ fontSize: 11, color: SLATE500, margin: 0 }}>{COMPANY.phone} · {COMPANY.support}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg, border: `1px solid ${st.fg}33` }}>{st.label}</span>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.18em', fontWeight: 600, marginTop: 12, color: GREEN }}>Hotel Voucher</p>
            <h1 style={{ fontWeight: 700, fontSize: 20, color: INK, margin: 0, fontFamily: 'Poppins, Inter, sans-serif' }}>Hotel Confirmation</h1>
            {ref && <p style={{ color: SLATE500, fontSize: 13, margin: '2px 0 0', fontFamily: 'ui-monospace, monospace' }}>{ref}</p>}
          </div>
        </header>

        <div style={{ padding: '24px 32px' }}>
          {/* meta strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 16, paddingBottom: 20, borderBottom: `1px solid ${LINE}` }}>
            <Field label="Voucher No." value={voucherNo} mono />
            <Field label="Booking ID" value={ref} mono />
            <Field label="Booking Date" value={fmt(b.created_at)} />
            <Field label="Booking Status" value={st.label} />
          </div>

          {/* Hotel Details */}
          <Sec>Hotel Details</Sec>
          <div style={box}>
            {/* No "Hotel Conf. #" chip. It printed String(ref).slice(-10) — the last
                ten characters of OUR OWN booking_reference, which is already shown in
                full as "Booking ID" two fields above (TJ207102912203 → #7102912203).
                The property has never seen that number, and nothing in either backend
                stores the hotel's real confirmation number to put there instead. The
                customer's copy prints no such chip on a by-ref voucher either, so the
                two sheets a guest can present at a front desk now agree. */}
            <div style={{ ...boxHead }}>
              <div>
                <h3 style={{ fontWeight: 700, color: INK, margin: 0 }}>
                  {hotel.name || stay.hotelName || 'Hotel'} {hotel.stars ? <span style={{ color: '#f59e0b' }}>{'★'.repeat(hotel.stars)}</span> : null}
                </h3>
                {hotel.address ? <p style={{ fontSize: 12, color: SLATE500, margin: '2px 0 0' }}>{hotel.address}</p> : null}
                {directionsUrl && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 600, color: GREEN, textDecoration: 'underline', textUnderlineOffset: 2 }}
                  >
                    Get Directions →
                  </a>
                )}
              </div>
            </div>
            <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 16 }}>
              <Field label="Check-in" value={`${fmt(stay.checkIn)}${hotel.checkInTime ? ` · ${hotel.checkInTime}` : ''}`} />
              <Field label="Check-out" value={`${fmt(stay.checkOut)}${hotel.checkOutTime ? ` · ${hotel.checkOutTime}` : ''}`} />
              <Field label="Total Stay" value={`${nights} Night${nights > 1 ? 's' : ''}`} />
              <Field label="Guests / Rooms" value={`${adults} Adult${adults > 1 ? 's' : ''}${children ? ` · ${children} Child` : ''} · ${roomCount} Room${roomCount > 1 ? 's' : ''}`} />
            </div>
            <div style={{ padding: '0 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 16 }}>
              <Field label="Room Type" value={stay.roomName || rooms[0]?.name || '—'} />
              <Field label="Meal Plan" value={meal} />
              <Field label="Cancellation" value={refundable === true ? 'Refundable' : refundable === false ? 'Non-refundable' : 'As per policy'} />
            </div>
          </div>

          {/* Rooms & Guests */}
          {rooms.length ? (
            <>
              <Sec>Rooms &amp; Guests</Sec>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {rooms.map((r, i) => (
                  <div key={i} style={box}>
                    <div style={{ ...boxHead, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <p style={{ fontWeight: 700, color: INK, fontSize: 13, margin: 0 }}>🛏 Room {i + 1} · {r.name || 'Room'}</p>
                      <span style={{ fontSize: 11, color: SLATE500, flexShrink: 0 }}>{r.adults || 0} Adult{(r.adults || 0) > 1 ? 's' : ''}{r.children ? ` · ${r.children} Child` : ''}</span>
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <p style={{ fontSize: 12, color: SLATE500, margin: '0 0 6px' }}><span style={{ fontWeight: 600, color: SLATE600 }}>Meal Plan:</span> {r.mealBasis || 'Room Only'}</p>
                      {(r.travellers || []).length ? (
                        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                          {r.travellers.map((t, ti) => (
                            /* Name + pax type only. A PAN is a tax identity and
                               has no business on a sheet handed to a hotel front
                               desk — it belongs on the invoice (client rule,
                               2026-09-10). The customer voucher does the same. */
                            <li key={ti} style={{ fontSize: 13, color: SLATE700 }}>
                              {guestLabel(t, ti)}
                              <span style={{ color: SLATE400 }}> · {(t.type || 'ADULT') === 'CHILD' ? 'Child' : 'Adult'}</span>
                            </li>
                          ))}
                        </ul>
                      ) : <p style={{ fontSize: 13, color: SLATE500, margin: 0 }}>—</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {/* Lead Guest & Contact */}
          <Sec>Lead Guest &amp; Contact</Sec>
          <Grid cols={3}>
            <Field label="Lead Passenger" value={leadName} />
            <Field label="Email" value={b.contact?.email} />
            <Field label="Mobile" value={b.contact?.phone} />
          </Grid>

          {/* Price Summary */}
          <Sec>Price Summary</Sec>
          <div style={{ ...box, fontSize: 14 }}>
            <Row k={`Room charges (${roomCount} room${roomCount > 1 ? 's' : ''} × ${nights} night${nights > 1 ? 's' : ''})`} v={inr2(num(fare.base_fare) + num(fare.processing_fee))} />
            <Row k="Taxes & fees" v={inr2(fare.gst_amount)} />
            <Row k={fare.convenience_label || 'Convenience fee'} v={inr2(fare.convenience_fee)} />
            {num(fare.coupon_discount) > 0 && <Row k={fare.discount_label || `Discount${fare.coupon_code ? ` (${fare.coupon_code})` : ''}`} v={`− ${inr2(fare.coupon_discount)}`} />}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: GREEN, color: INK, fontWeight: 700 }}>
              <span>Total Paid</span><span>{inr2(fare.total_paid)}</span>
            </div>
          </div>

          {/* Cancellation Policy */}
          {penalties.length ? (
            <>
              <Sec>Cancellation Policy</Sec>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden' }}>
                <thead style={{ background: SLATE50, color: SLATE500, textAlign: 'left' }}>
                  <tr>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Cancel on / after</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600 }}>Up to</th>
                    <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {penalties.map((pn, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${SLATE50}` }}>
                      <td style={{ padding: '8px 12px' }}>{fmt(pn.from)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmt(pn.to)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{num(pn.amount) > 0 ? inr2(pn.amount) : 'Free'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {/* Booking Notes */}
          {notes.length ? (
            <>
              <Sec>Booking Notes</Sec>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {notes.map((g, gi) => (
                  <div key={gi}>
                    <h4 style={{ fontWeight: 700, color: INK, fontSize: 13, margin: 0 }}>{g.type}</h4>
                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(g.items || []).map((it, ii) => (
                        <p key={ii} style={{ fontSize: 12.5, color: SLATE600, lineHeight: 1.5, margin: 0 }}>
                          {it.label ? <span style={{ fontWeight: 600, color: SLATE700 }}>{it.label}: </span> : null}{it.text}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {/* General Terms & Conditions */}
          <Sec>General Terms &amp; Conditions</Sec>
          <ol style={{ paddingLeft: 20, fontSize: 12.5, color: SLATE600, lineHeight: 1.6, margin: 0 }}>
            {HOTEL_BOOKING_TERMS.map((t, i) => <li key={i} style={{ marginBottom: 4 }}>{t}</li>)}
          </ol>

          <p style={{ marginTop: 24, fontSize: 11, color: SLATE400, textAlign: 'center' }}>This is a Computer Generated Voucher, No Signature required.</p>
        </div>
      </article>
    </>
  );
}
